import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { apiErr } from "@/lib/api";
import { Link } from "react-router-dom";
import { Tag, Search } from "lucide-react";
import { toast } from "sonner";

export default function RicercaTag() {
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/tags").then((r) => setTags(r.data)).catch((e) => toast.error(apiErr(e))); }, []);

  const search = async (t) => {
    setSelected(t);
    const { data } = await api.get("/cartelle/search", { params: { tag: t } });
    setResults(data);
  };

  const customSearch = async () => {
    if (!q.trim()) return;
    let t = q.trim();
    if (!t.startsWith("#")) t = "#" + t;
    await search(t);
  };

  return (
    <AppShell title="Ricerca per tag" subtitle="Trova cartelle cliniche per tag diagnostico (es. #glaucoma, #cataratta)">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
          <h3 className="font-bold text-brand-navy mb-3 flex items-center gap-2"><Tag size={16} className="text-brand-azure" /> Tag presenti</h3>
          <div className="flex gap-2 mb-3">
            <input data-testid="tag-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && customSearch()} placeholder="cerca tag…" className="flex-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
            <button data-testid="tag-search-btn" onClick={customSearch} className="bg-brand-navy text-white px-3 py-2 rounded-lg"><Search size={14} /></button>
          </div>
          <div className="space-y-1 max-h-[500px] overflow-y-auto" data-testid="tags-cloud">
            {tags.length === 0 ? <div className="text-xs text-slate-400 italic">Nessun tag in archivio</div> :
              tags.map((t) => (
                <button
                  key={t.tag}
                  data-testid={`tag-pick-${t.tag.replace("#", "")}`}
                  onClick={() => search(t.tag)}
                  className={`w-full flex items-center justify-between p-2 rounded-lg text-sm font-bold transition ${selected === t.tag ? "bg-brand-navy text-white" : "bg-slate-50 hover:bg-brand-azure/10 text-brand-navy"}`}
                >
                  <span>{t.tag}</span>
                  <span className="text-xs opacity-70">{t.count}</span>
                </button>
              ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
            <h3 className="font-bold text-brand-navy mb-3">{selected ? `Risultati per ${selected}` : "Seleziona un tag"}</h3>
            <div className="space-y-2" data-testid="tag-results">
              {results.length === 0 && selected && <div className="text-center text-slate-500 py-6">Nessuna cartella con questo tag</div>}
              {results.map((r) => (
                <Link key={r.appointment_id} to={`/cartella/${r.appointment_id}`} data-testid={`result-${r.appointment_id}`} className="block border border-[#E2E8F0] rounded-lg p-4 hover:border-brand-azure hover:bg-brand-azure/5 transition">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold text-brand-navy">{r.context?.patient_name || "Paziente"}</div>
                      <div className="text-xs text-slate-500">{r.context?.starts_at ? new Date(r.context.starts_at).toLocaleString("it-IT") : ""} · {r.context?.clinic_name}</div>
                      <div className="text-sm text-slate-700 mt-1 line-clamp-2">{r.diagnosi || "—"}</div>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                      {(r.tags || []).map((t) => <span key={t} className="text-[10px] font-bold bg-brand-navy text-white px-1.5 py-0.5 rounded">{t}</span>)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
